import { Card, Col, Row, Form as BForm, Spinner, Button } from '@themesberg/react-bootstrap'
import { ErrorMessage, Form, Formik } from 'formik'
import React, { useState } from 'react'
import { getSendEmailSchema } from './schema'
import { sendUserEmailStart } from '../../../../store/redux-slices/tenantUsers'
import { useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons'
import Trigger from '../../../../components/OverlayTrigger'
import CodepenEditor from '../../../../components/CodeEditor'
import ReactQuill from 'react-quill'

const SendEmail = ({
  userData,
  isTenant = false,
  loading,
  emailTemplates = {},
  tenantDetails = {}
}) => {
  const dispatch = useDispatch()
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('default')
  const [templateHTML, setTemplateHTML] = useState('')
  const userLang = userData?.preferredLanguage || 'EN'

  // Find template by id
  const findTemplateById = (id) => {
    let found = null
    Object.keys(emailTemplates).forEach((group) => {
      const tpl = (emailTemplates[group] || []).find(t => String(t.emailTemplateId) === id)
      if (tpl) found = tpl
    })
    return found
  }

  // Helper to get autofilled tenant fields
  const getTenantFieldValue = (key) => {
    switch (key) {
      case 'tenantName':
        return tenantDetails?.name || ''
      case 'tenantUrl':
        return tenantDetails?.domain || ''
      case 'tenantLogo':
        return tenantDetails?.tenantConfig?.logoUrl || ''
      default:
        return ''
    }
  }
  // Helper to prettify label
  const prettifyLabel = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
  
  // Include these fields as autofilled
  const autoFields = ['tenantName', 'tenantUrl', 'tenantLogo']

  return (
    <Card className='p-3'>
      <Row>
        <Col className='d-flex'>
          <h3>Send Email</h3>
        </Col>
      </Row>

      <Formik
        enableReinitialize
        initialValues={{
          subject: '',
          content: '',
          isSupportEmail: !!userData?.licenseId,
          dynamicData: {}
        }}
        validationSchema={getSendEmailSchema(
          selectedTemplateKey !== 'default' ? findTemplateById(selectedTemplateKey) : null,
          userData
        )}
        onSubmit={(formValues, { resetForm }) => {
          const { dynamicData, ...rest } = formValues
          let contentToSend = formValues.content

          if (selectedTemplateKey !== 'default') {
            contentToSend = templateHTML
            const dynamicDataWithTenant = {
              ...dynamicData,
              tenantName: getTenantFieldValue('tenantName'),
              tenantUrl: getTenantFieldValue('tenantUrl'),
              tenantLogo: getTenantFieldValue('tenantLogo')
            }
            Object.entries(dynamicDataWithTenant).forEach(([key, value]) => {
              const regex = new RegExp(`{{{${key}}}}`, 'g')
              contentToSend = contentToSend.replace(regex, value)
            })
          }

          dispatch(sendUserEmailStart({
            data: {
              ...rest,
              content: contentToSend,
              userId: userData?.userId,
            },
            isTenant,
            resetForm
          }))
        }}
      >
        {({
          values,
          handleChange,
          handleSubmit,
          handleBlur,
          setFieldValue
        }) => {
          // Handle template change
          const handleTemplateChange = (e) => {
            const templateId = e.target.value
            setSelectedTemplateKey(templateId)
            if (templateId === 'default') {
              setFieldValue('subject', '')
              setFieldValue('content', '')
              setFieldValue('dynamicData', {})
              setTemplateHTML('')
            } else {
              const tpl = findTemplateById(templateId)
              // Set HTML for CodepenEditor and Formik
              const codeObj = tpl?.templateCode || {}
              const html = codeObj[userLang] || codeObj['EN'] || Object.values(codeObj)[0] || ''
              setFieldValue('content', html)
              setTemplateHTML(html)
              // Set dynamicData initial values, always include tenantName
              const dynObj = {}
              let dynKeys = tpl.dynamicData && Array.isArray(tpl.dynamicData) ? [...tpl.dynamicData] : []
              autoFields.forEach(field => {
                if (!dynKeys.includes(field)) dynKeys.push(field)
              })
              dynKeys.forEach(key => {
                if (autoFields.includes(key)) {
                  dynObj[key] = getTenantFieldValue(key)
                } else {
                  dynObj[key] = ''
                }
              })
              setFieldValue('dynamicData', dynObj)
            }
          }

          const handleTemplateEditorChange = (html) => {
            setTemplateHTML(html)
            setFieldValue('content', html)
          }

          const handleDynamicFieldChange = (key, value, setFieldValue, values) => {
            setFieldValue('dynamicData', {
              ...values.dynamicData,
              [key]: value
            })
          }

          const selectedTemplate = selectedTemplateKey !== 'default'
            ? findTemplateById(selectedTemplateKey)
            : null

          // Always show tenantName for non-default templates
          let dynamicKeys = []
          if (selectedTemplateKey !== 'default') {
            dynamicKeys = selectedTemplate?.dynamicData ? [...selectedTemplate.dynamicData] : []
            autoFields.forEach(field => {
              if (!dynamicKeys.includes(field)) dynamicKeys.push(field)
            })
          }

          return (
            <Form>
              {/* Dropdown for templates */}
              <Row className='mb-3'>
                <Col md={6}>
                  <BForm.Label>Custom Email Template</BForm.Label>
                  <BForm.Select
                    value={selectedTemplateKey}
                    onChange={handleTemplateChange}
                    className='form-control'
                  >
                    <option value="default">Default</option>
                    {Object.keys(emailTemplates).map((key) =>
                      (emailTemplates[key] || [])
                        .filter(tpl => !(tpl.dynamicData && tpl.dynamicData.includes('link')))
                        .map((tpl) => (
                          <option key={tpl.emailTemplateId} value={tpl.emailTemplateId}>
                            {tpl.label}
                          </option>
                        ))
                    )}
                  </BForm.Select>
                </Col>
              </Row>

              {!!isTenant && (
                <Row className='mt-3'>
                  <Col xs='auto' className='d-flex mb-3 justify-content-center align-items-center'>
                    <BForm.Label className='mb-0 me-4'> Send via Support Email
                      <Trigger message={`If checked, the email will be sent from the support email. If unchecked, it will be sent from the no-reply email`}>
                        <div className='d-inline-block ms-2'>
                          <FontAwesomeIcon icon={faCircleInfo} />
                        </div>
                      </Trigger>
                    </BForm.Label>
                    <BForm.Switch
                      name='isSupportEmail'
                      checked={
                        userData?.licenseId
                          ? true
                          : values.isSupportEmail
                      }
                      disabled={!!userData?.licenseId}
                      onChange={e => {
                        if (!userData?.licenseId) {
                          setFieldValue('isSupportEmail', e.target.checked)
                        }
                      }}
                    />
                  </Col>
                  <Col />
                </Row>
              )}

              <Row className='mt-3 mb-3'>
                <Col>
                  <BForm.Label>Subject
                    <span className='text-danger'> *</span>
                  </BForm.Label>
                  <BForm.Control
                    type='text'
                    name='subject'
                    placeholder='Enter Subject'
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <ErrorMessage
                    component='div'
                    name='subject'
                    className='text-danger'
                  />
                </Col>
                <Col />
              </Row>
              {/* Dynamic fields */}
              {selectedTemplateKey !== 'default' && dynamicKeys.length > 0 && (
                <Row className='mb-3'>
                  {dynamicKeys.map((key) => (
                    <Col md={6} key={key} className='mb-2'>
                      <BForm.Label>
                        {prettifyLabel(key)}
                        {autoFields.includes(key) ? ' (Auto-filled)' : ' (Required)'}
                        {key === 'kycLink' && (
                          <Trigger message='KYC link indicates the Account verification page for the user'>
                            <div className='d-inline-block ms-2'> 
                              <FontAwesomeIcon icon={faCircleInfo} />
                            </div>
                          </Trigger>
                        )}
                      </BForm.Label>
                      <BForm.Control
                        type='text'
                        name={`dynamicData.${key}`}
                        value={
                          autoFields.includes(key)
                            ? getTenantFieldValue(key)
                            : (values.dynamicData?.[key] || '')
                        }
                        onChange={e => handleDynamicFieldChange(key, e.target.value, setFieldValue, values)}
                        disabled={key === 'tenantName'}
                        placeholder={autoFields.includes(key)
                          ? 'Auto-filled'
                          : `Enter ${prettifyLabel(key)}`}
                        required={!autoFields.includes(key)}
                      />
                      <ErrorMessage
                        component='div'
                        name={`dynamicData.${key}`}
                        className='text-danger'
                      />
                    </Col>
                  ))}
                </Row>
              )}

              {/* Preview */}
              {selectedTemplateKey !== 'default' && (
                <Row>
                  <CodepenEditor
                    HTML={templateHTML}
                    setTemplate={handleTemplateEditorChange}
                    dynamicData={JSON.stringify({
                      ...values.dynamicData,
                      tenantName: getTenantFieldValue('tenantName'),
                      tenantUrl: getTenantFieldValue('tenantUrl'),
                      tenantLogo: getTenantFieldValue('tenantLogo')
                    })}
                    initial='HTML'
                    mobileQuery={800}
                    height='80vh'
                    setRequiredKeyData={() => { }}
                    test={false}
                  />
                </Row>
              )}

              {/* Content field for default */}
              {selectedTemplateKey === 'default' && (
                <Row className='mt-3 mb-4'>
                  <Col className='mb-4'>
                    <BForm.Label>Content
                      <span className='text-danger'> *</span>
                    </BForm.Label>
                    <ReactQuill
                      name='content'
                      value={values?.content}
                      style={{ height: '200px' }}
                      onChange={(e) => setFieldValue('content', e)}
                      preserveWhitespace
                    />
                    <ErrorMessage
                      component='div'
                      name='content'
                      className='text-danger'
                    />
                  </Col>
                </Row>
              )}

              <div className='mt-4 d-flex justify-content-end align-items-center'>
                <Button
                  variant='outline-success'
                  onClick={handleSubmit}
                  className='ml-2'
                >
                  Submit
                  {loading && (
                    <Spinner
                      as='span'
                      animation='border'
                      size='sm'
                      role='status'
                      aria-hidden='true'
                    />
                  )}
                </Button>
              </div>
            </Form>
          )
        }}
      </Formik>
    </Card>
  )
}

export default React.memo(SendEmail)